import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Box } from '@mui/material';
import { EditRounded } from '@mui/icons-material';
import Button from '@/components/CustomButtons/Button';
import ManageFiles from './ManageFiles';
import { modalStyle } from '@/views/Clients/Claim';
import { updateRefund } from './UpdateRefundModal';
import CardHeader from '@/components/Card/CardHeader';
import CardBody from '@/components/Card/CardBody';
import {
  ENTITY_TYPE,
  REFUND_METHOD,
  REFUND_STATUSES,
  REFUND_SUB_STATUS,
  REFUND_TYPES,
  TRANSACTION_STATUSES,
  CHECK_TYPE_DB,
} from '@/utility';
import { useConfirmation } from '@/context/ConfirmationContext';
import { usePostHog } from 'posthog-js/react';
import CheckTypeSelector from '@/views/Facilities/CheckTypeSelector';

export default function IssueCheckModal({
  currentCheckRefund,
  setCurrentCheckRefund,
  setCurrentInfo,
  setOriginalInfo,
  setModalErr,
  processPayment,
  setPartialState,
}) {
  const { t } = useTranslation();
  const posthog = usePostHog();
  const { requestConfirmation } = useConfirmation();
  const isCustomPaymentTypesEnabled = posthog?.isFeatureEnabled(
    'new-custom-payment-types-workflow',
  );
  const isEcheckWorkflowEnabled = posthog?.isFeatureEnabled(
    'new-echeck-workflow',
  );

  const [attachments, setAttachments] = useState({
    internalAttachments: {
      addFiles: [],
      deletedFiles: [],
    },
    payeeAttachments: {
      addFiles: [],
      deletedFiles: [],
    },
  });

  const [checkType, setCheckType] = useState(
    currentCheckRefund?.check_type || CHECK_TYPE_DB.PHYSICAL,
  );

  const getWarningMessages = (isForConfirm) => {
    const messages = [];
    if (
      currentCheckRefund.refund_sub_status ===
        REFUND_SUB_STATUS.PAYEE_EXCEPTION &&
      !attachments.payeeAttachments.addFiles.length
    ) {
      messages.push(t('issueCheck.warnings.supportingDocumentsMissing'));
    }
    if (
      currentCheckRefund.refund_sub_status ===
        REFUND_SUB_STATUS.PAYEE_EXCEPTION &&
      !currentCheckRefund.memo
    ) {
      messages.push(t('issueCheck.warnings.addendaMissing'));
    }

    if (
      currentCheckRefund.refund_sub_status ===
        REFUND_SUB_STATUS.ADDENDA_MISSING &&
      !currentCheckRefund.memo
    ) {
      messages.push(t('issueCheck.warnings.addendaMissing'));
    }

    if (
      (currentCheckRefund.refund_sub_status ===
        REFUND_SUB_STATUS.PAYEE_EXCEPTION &&
        (!attachments.payeeAttachments.addFiles.length ||
          !currentCheckRefund.memo)) ||
      !messages.length
    ) {
      messages.push(
        isForConfirm
          ? t('issueCheck.warnings.proceedConfirm')
          : t('issueCheck.warnings.proceedIssueCheck'),
      );
    }

    return (
      <div
        style={{
          color: 'red',
          padding: '0',
          margin: 0,
          fontSize: '15px',
          marginBottom: '20px',
        }}>
        <ul style={{ paddingLeft: '20px' }}>
          {messages.map((message, index) => (
            <li key={index}>{message}</li>
          ))}
        </ul>
      </div>
    );
  };

  const getWarningMessageText = () => {
    const messages = [];
    if (
      currentCheckRefund.refund_sub_status ===
        REFUND_SUB_STATUS.PAYEE_EXCEPTION &&
      !attachments.payeeAttachments.addFiles.length
    ) {
      messages.push(t('issueCheck.warnings.supportingDocumentsMissing'));
    }
    if (
      currentCheckRefund.refund_sub_status ===
        REFUND_SUB_STATUS.PAYEE_EXCEPTION &&
      !currentCheckRefund.memo
    ) {
      messages.push(t('issueCheck.warnings.addendaMissing'));
    }

    if (
      currentCheckRefund.refund_sub_status ===
        REFUND_SUB_STATUS.ADDENDA_MISSING &&
      !currentCheckRefund.memo
    ) {
      messages.push(t('issueCheck.warnings.addendaMissing'));
    }

    if (
      (currentCheckRefund.refund_sub_status ===
        REFUND_SUB_STATUS.PAYEE_EXCEPTION &&
        (!attachments.payeeAttachments.addFiles.length ||
          !currentCheckRefund.memo)) ||
      !messages.length
    ) {
      messages.push(t('issueCheck.warnings.proceedConfirm'));
    }

    return (
      <div
        style={{
          color: 'red',
          padding: '0',
          margin: 0,
          fontSize: '15px',
        }}>
        <ul style={{ paddingLeft: '20px' }}>
          {messages.map((message, index) => (
            <li key={index}>{message}</li>
          ))}
        </ul>
      </div>
    );
  };

  const handleConfirm = async () => {
    try {
      setPartialState({ loading: true });
      await updateRefund({
        id: currentCheckRefund.id,
        attachments,
        disableMessage: true,
      });

      await processPayment({ id: currentCheckRefund.id, checkType });
      setPartialState({ loading: false });
    } catch (err) {
      console.log(err);
      setPartialState({ loading: false });
      setModalErr(err.response?.data?.message);
    }
  };

  const onIssueCheck = async () => {
    try {
      requestConfirmation(getWarningMessageText(), handleConfirm);
    } catch (err) {
      console.log(err);
      setPartialState({ loading: false });
      setModalErr(err.response?.data?.message);
    }
  };

  const isCheckClaimable =
    currentCheckRefund.refund_status === REFUND_STATUSES.UNCLAIMED ||
    currentCheckRefund.refund_status === REFUND_STATUSES.ESCHEATED ||
    currentCheckRefund.refund_status === REFUND_STATUSES.EXCEPTION ||
    (currentCheckRefund.refund_status === REFUND_STATUSES.REQUESTED &&
      currentCheckRefund.transaction_status ===
        TRANSACTION_STATUSES.ACH_RETURNED) ||
    (currentCheckRefund.refund_status === REFUND_STATUSES.REQUESTED &&
      currentCheckRefund.transaction_status ===
        TRANSACTION_STATUSES.ACH_RETURN_AVAILABLE_TO_RECLAIM);

  return (
    <>
      <Modal
        open={!!currentCheckRefund}
        onClose={() => setCurrentCheckRefund(null)}>
        <Box
          sx={modalStyle}
          className='makeStyles-card-96'
          noValidate
          autoComplete='off'>
          <CardHeader style={{ transform: 'translateY(-50%)' }} color='primary'>
            <h4>
              {currentCheckRefund.refund_type === REFUND_TYPES.REGULAR
                ? t('issueCheck.title.consumerPayment')
                : currentCheckRefund.refund_sub_status ===
                  REFUND_SUB_STATUS.PAYEE_EXCEPTION
                ? t('issueCheck.title.payeeException')
                : t('issueCheck.title.nonPayeeException')}
            </h4>
          </CardHeader>
          <CardBody>
            {(isCustomPaymentTypesEnabled
              ? currentCheckRefund.entity_type === ENTITY_TYPE.BUSINESS
              : currentCheckRefund.refund_type === REFUND_TYPES.INSURANCE) &&
              currentCheckRefund.refund_status === REFUND_STATUSES.EXCEPTION &&
              getWarningMessages(false)}
            {currentCheckRefund.refund_status === REFUND_STATUSES.REQUESTED &&
            currentCheckRefund.refund_method === REFUND_METHOD.CHECK
              ? t('issueCheck.checkAlreadyRequested')
              : !isCheckClaimable
              ? t('issueCheck.checkCannotBeIssued')
              : null}

            {currentCheckRefund && (
              <div>
                <p style={{ marginBottom: '7px', fontWeight: 'bold' }}>
                  {t('issueCheck.addressOnFile')}{' '}
                  <a
                    onClick={() => {
                      setCurrentInfo(currentCheckRefund);
                      setOriginalInfo(currentCheckRefund);
                      setModalErr('');
                    }}>
                    <EditRounded
                      style={{ cursor: 'pointer' }}
                      fontSize='small'
                    />
                  </a>
                </p>
                <p style={{ marginBottom: '7px' }} className='page-desc'>
                  {(
                    isCustomPaymentTypesEnabled
                      ? currentCheckRefund.entity_type === ENTITY_TYPE.BUSINESS
                      : currentCheckRefund.refund_type ===
                        REFUND_TYPES.INSURANCE
                  )
                    ? currentCheckRefund.business_payee_name
                    : currentCheckRefund.first_name +
                      ' ' +
                      currentCheckRefund.last_name}
                </p>
                <p style={{ marginBottom: '7px' }} className='page-desc'>
                  {currentCheckRefund.refund_to_address_line1},{' '}
                  {currentCheckRefund.refund_to_address_line2}
                </p>
                <p style={{ marginBottom: '7px' }} className='page-desc'>
                  {currentCheckRefund.refund_to_city},{' '}
                  {currentCheckRefund.refund_to_state},{' '}
                  {currentCheckRefund.refund_to_zip}{' '}
                </p>

                <div>
                  <p style={{ marginBottom: '7px', fontWeight: 'bold' }}>
                    {t('issueCheck.memo')}
                  </p>
                  <p style={{ marginBottom: '7px' }} className='page-desc'>
                    {currentCheckRefund.memo || t('issueCheck.na')}
                  </p>
                </div>
              </div>
            )}
            {isEcheckWorkflowEnabled && (
              <CheckTypeSelector
                value={checkType}
                onChange={setCheckType}
                disabled={false}
              />
            )}
            {(isCustomPaymentTypesEnabled
              ? currentCheckRefund.enable_payee_attachments ||
                currentCheckRefund.enable_internal_attachments
              : currentCheckRefund.refund_type === REFUND_TYPES.INSURANCE) && (
              <ManageFiles
                refund={currentCheckRefund}
                onChange={setAttachments}
                isInternalAttachmentsEnabled={
                  isCustomPaymentTypesEnabled
                    ? currentCheckRefund?.enable_internal_attachments
                    : false
                }
                isPayeeAttachmentsEnabled={
                  isCustomPaymentTypesEnabled
                    ? currentCheckRefund?.enable_payee_attachments
                    : true
                }
                isCustomPaymentTypesEnabled={isCustomPaymentTypesEnabled}
              />
            )}
            {!!attachments.payeeAttachments.addFiles.length && (
              <p
                style={{
                  fontSize: '15px',
                  marginTop: '20px',
                  fontWeight: 'bold',
                }}>
                {t('issueCheck.attachmentDisclaimer')}
              </p>
            )}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                marginTop: '30px',
              }}>
              <Button
                onClick={onIssueCheck}
                type='button'
                variant='contained'
                disabled={!isCheckClaimable}
                color='rose'>
                {t('issueCheck.issueCheckButton')}
              </Button>
            </div>
          </CardBody>
        </Box>
      </Modal>
    </>
  );
}
